// oEmbed HTML for a given post is effectively static (it's boilerplate
// wrapper markup plus the permalink, not the live post content itself -
// Instagram's embed.js fetches the actual content fresh client-side
// regardless), so it's a safe, low-risk thing to cache. Cached per-URL
// (rather than per-product's whole URL list) so adding one new video to a
// product doesn't force re-fetching every other one that's already cached,
// and so the same post reused across products shares a single cache entry.
//
// Declared here, before customElements.define() below, rather than down by
// getEmbeds() where it's used - customElements.define() synchronously
// upgrades any <ugc-section> already in the DOM, which calls getEmbeds()
// before the rest of this script has finished running. A const declared
// further down would still be in its temporal dead zone at that point,
// throwing "Cannot access before initialization" - confirmed via live
// testing.
const UGC_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const UGC_CACHE_PREFIX = 'casdim_ugc_cache_';

class UGC extends HTMLElement {
  constructor() {
    super();
    this.isInitialized = false;
    this.urls = [];
  }

  async connectedCallback() { 
    // Only initialize once to prevent multiple event listeners
    if (!this.isInitialized) {
      await initUGC(this);
      this.isInitialized = true;
    }
  }
}
customElements.define('ugc-section', UGC);


/**
 * Initialises a UGC Section by embedding the provided Instagram and Facebook
 * URLs into the section.
 *
 * Note: TikTok support was attempted and reverted - confirmed via live
 * testing that TikTok's embed SDK destroys its own just-rendered iframe if
 * the blockquote is reparented afterward, which directly conflicts with the
 * staging pattern below (build off-screen, wait, then move into place) that
 * Instagram's flow relies on. Supporting both would need TikTok on a fully
 * separate code path (built directly in its final position, never moved) -
 * not worth the added complexity given most UGC content here is Instagram.
 * @param {HTMLElement} element - the UGC section that needs to be initialised.
 * Assumes that it is already not initialised.
 */
async function initUGC(element) {
  element.urls = Array.from(new Set(element.dataset.urls.split(',').map(url => url.trim()).filter(Boolean)));

  const embeds = await getEmbeds(element.urls);

  const contentGrid = element.querySelector('.content-grid');
  const richCarouselEl = element.querySelector('rich-carousel');
  const track = contentGrid || richCarouselEl?.querySelector('.rich-carousel__track');

  if (!track) {
    return;
  }

  if (!embeds.some(html => html?.includes('instagram-media'))) {
    track.innerHTML = embeds.join('');
    if (richCarouselEl) {
      window.refreshRichCarouselSlides?.(richCarouselEl);
    }
    return;
  }

  // Instagram's embed.js transforms each blockquote into its real iframe
  // asynchronously with no completion callback/promise exposed, and (confirmed
  // via live inspection) it does NOT atomically replace the blockquote either
  // - it inserts the finished iframe as a new sibling first, and only removes
  // the original blockquote afterwards once further internal processing
  // completes. waitForInstagramEmbedsToSettle() below waits out that full
  // cleanup rather than resolving the moment the iframe appears.
  //
  // Staging the embeds in a detached, off-screen container first also keeps
  // this whole transform isolated from the live track - the track only ever
  // receives the finished, settled elements, moved over in one shot, so it's
  // never wrapped prematurely by the carousel's own MutationObserver and
  // never left holding a stale empty wrapper.
  const stage = document.createElement('div');
  stage.style.cssText = `position: absolute; visibility: hidden; pointer-events: none; left: -9999px; top: 0; width: ${track.offsetWidth || richCarouselEl?.offsetWidth || 600}px;`;
  document.body.appendChild(stage);
  stage.innerHTML = embeds.join('');

  // Capture the exact blockquote element references now, before .process()
  // runs - see waitForInstagramEmbedsToSettle()'s doc comment for why.
  const instagramBlockquotes = Array.from(stage.querySelectorAll('blockquote.instagram-media'));

  await loadInstagramEmbedScript();
  window.instgrm.Embeds.process(stage);
  await waitForInstagramEmbedsToSettle(instagramBlockquotes);

  // Defensive backstop: if waitForInstagramEmbedsToSettle had to give up on
  // the timeout instead of confirming a clean settle, drop any leftover
  // blockquote so we never show a half-processed placeholder as a slide.
  // (Not scoped to a specific class, since Instagram renames the
  // blockquote's class partway through its own cleanup - see comment above.)
  Array.from(stage.querySelectorAll('blockquote')).forEach(bq => bq.remove());

  track.innerHTML = '';
  Array.from(stage.children).forEach(child => track.appendChild(child));
  stage.remove();

  if (richCarouselEl) {
    window.refreshRichCarouselSlides?.(richCarouselEl);
  }
}

/**
 * Polls until every given Instagram blockquote element has been removed from
 * the DOM (or a timeout elapses), since embed.js processes blockquotes
 * asynchronously with no way to await completion directly.
 *
 * Confirmed via live testing: embed.js does NOT atomically replace each
 * blockquote with its iframe. It inserts the finished iframe as a new
 * sibling first (already marked instagram-media-rendered), and only removes
 * the original blockquote afterwards, once further internal processing
 * completes - and it renames the blockquote's own class along the way
 * (dropping the plain "instagram-media" class), so a class-based "still
 * pending" check can't reliably detect that removal is still outstanding.
 * Tracking the exact element references (captured before .process() runs)
 * sidesteps that entirely.
 * @param {HTMLElement[]} blockquotes - the original Instagram blockquote elements to watch.
 * @param {Number} [timeoutMs] - how long to poll before giving up.
 * @returns {Promise<void>}
 */
function waitForInstagramEmbedsToSettle(blockquotes, timeoutMs = 8000) {
  return new Promise(resolve => {
    const start = Date.now();
    const check = () => {
      const stillPresent = blockquotes.some(bq => bq.isConnected);
      if (!stillPresent || Date.now() - start > timeoutMs) {
        resolve();
      } else {
        setTimeout(check, 150);
      }
    };
    check();
  });
}

let instagramEmbedScriptPromise = null;

/**
 * Loads Instagram's embed.js script exactly once, even if called from
 * multiple UGC sections on the same page.
 * @returns {Promise<void>} Resolves once embed.js is loaded and window.instgrm is available.
 */
function loadInstagramEmbedScript() {
  if (window.instgrm) {
    return Promise.resolve();
  }
  if (!instagramEmbedScriptPromise) {
    instagramEmbedScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://www.instagram.com/embed.js';
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return instagramEmbedScriptPromise;
}


/**
 * Removes expired UGC embed cache entries from localStorage, so it doesn't
 * grow unbounded as more distinct posts get cached across product pages.
 * @returns {void}
 */
function pruneUgcCache() {
  const now = Date.now();
  for (let i = window.localStorage.length - 1; i >= 0; i--) {
    const key = window.localStorage.key(i);
    if (!key?.startsWith(UGC_CACHE_PREFIX)) {
      continue;
    }
    try {
      const entry = JSON.parse(window.localStorage.getItem(key));
      if (!entry || now - entry.timestamp > UGC_CACHE_TTL_MS) {
        window.localStorage.removeItem(key);
      }
    } catch {
      window.localStorage.removeItem(key);
    }
  }
}

/**
 * Gets the HTML embeds for the provided Instagram and Facebook URLs.
 *
 * @param {String[]} urls - An array of URLs to embed. Are assumed to be already
 * valid Instagram or Facebook URLs.
 *
 * @return {Promise<String[]>} - A promise that resolves to an array of HTML embed strings
 * for the provided URLs. The order of the returned embeds will match the order of
 * the provided URLs.
 */
function getEmbeds(urls) {
  return Promise.all(urls.map(async url => {
    if (url.includes('instagram.com') || url.includes('facebook.com')) {
      const cacheKey = `${UGC_CACHE_PREFIX}${url}`;

      try {
        const cached = JSON.parse(window.localStorage.getItem(cacheKey));
        if (cached && Date.now() - cached.timestamp < UGC_CACHE_TTL_MS) {
          return cached.data;
        }
      } catch {
        // Ignore malformed cache entries and fall through to a fresh fetch.
      }

      return fetch(`https://graph.facebook.com/v26.0/instagram_oembed?url=${url}&omitscript=true&hidecaption=true`)
        .then(res => res.json())
        .then(json => {
          try {
            pruneUgcCache();
            window.localStorage.setItem(cacheKey, JSON.stringify({data: json.html, timestamp: Date.now()}));
          } catch {
            // localStorage may be full or unavailable (e.g. private browsing) - caching is best-effort.
          }
          return json.html;
        });
    }
  }));
}
